import { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import {
  MASK_NUMERIC,
  MASK_PERCENTAGE,
  StatefulControlledTextField,
} from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import {
  DEBT_SCHEDULE_CATEGORIES,
  DEBT_SCHEDULE_DAY_COUNT_CONVENTIONS,
  DEBT_SCHEDULE_PAYMENT_FREQUENCIES,
  DEBT_SCHEDULE_PURPOSE_LIST,
} from "components/Financial/helpers";
import { useSnackbar } from "notistack";
import { createDateAcceptHandler, parseDateValue } from "utility/dateHelpers";

export default function EquipmentInventoryLoanEditDialog({ onClose, loan, onChange }) {
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const currentYear = globalState?.crop_year;
  const [state, setState] = useState(loan);
  const [formErrors, setFormErrors] = useState({});
  const formMaxWidthShort = "1200px";

  useEffect(() => {
    setState(loan);
  }, [loan]);

  const handleChangeState = (event, value) => {
    const eventValue = event.target.value;
    const eventName = event.target.name;
    const newValue = value ?? eventValue;

    if (formErrors[eventName]) {
      setFormErrors((prev) => ({ ...prev, [eventName]: undefined }));
    }

    setState((prev) => ({
      ...prev,
      [eventName]: newValue,
    }));
  };

  const handleChangeStateSelect = (event) => {
    const eventName = event.target.name;

    if (formErrors[eventName]) {
      setFormErrors((prev) => ({ ...prev, [eventName]: undefined }));
    }

    setState((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleDateAccept = (fieldName) =>
    createDateAcceptHandler({
      onChange: handleChangeState,
      onError: (message) => {
        enqueueSnackbar(message, {
          variant: "error",
          preventDuplicate: true,
        });
      },
      format: "YYYY-MM-DD",
      fieldName,
      clearErrors: (field) => {
        if (formErrors[field]) {
          setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      },
    });

  const validateForm = () => {
    const errors = {};
    const fields = [];
    if (!state?.creditor?.trim()) {
      errors.creditor = "Required";
      fields.push("Creditor");
    }
    if (!state?.purpose) {
      errors.purpose = "Required";
      fields.push("Purpose");
    }
    if (!state?.category) {
      errors.category = "Required";
      fields.push("Category");
    }

    setFormErrors(errors);
    return fields;
  };
  const handleSave = async () => {
    const fields = validateForm();
    if (fields.length > 0) {
      enqueueSnackbar(`Please correct the following fields: ${fields.join(", ")}`, { variant: "error" });
      return;
    }
    onChange(state);
    onClose();
  };
  return (
    <>
      <DialogTitle>{loan?.id ? "Edit Loan" : "Add Loan"}</DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="selectYearLabel">Year</InputLabel>
                <Select
                  labelId="selectYearLabel"
                  value={state?.year || currentYear}
                  name="year"
                  onChange={handleChangeStateSelect}
                  label="Year"
                >
                  {getBudgetYears()?.map((currentItem, index) => (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Name"
                  name="name"
                  defaultValue={state?.name || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Creditor"
                  name="creditor"
                  isRequired
                  defaultValue={state?.creditor || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  errorText={formErrors.creditor}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={!!formErrors.purpose}>
                <InputLabel id="selectPurposeLabel">Purpose</InputLabel>
                <Select
                  labelId="selectPurposeLabel"
                  value={state?.purpose || ""}
                  name="purpose"
                  onChange={handleChangeStateSelect}
                  label="Purpose"
                  required
                >
                  {DEBT_SCHEDULE_PURPOSE_LIST.map((currentItem, index) => (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.purpose && <FormHelperText>{formErrors.purpose}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel id="selectCategoryLabel">Category</InputLabel>
                <Select
                  labelId="selectCategoryLabel"
                  value={state?.category || ""}
                  name="category"
                  onChange={handleChangeStateSelect}
                  label="Category"
                  required
                >
                  {DEBT_SCHEDULE_CATEGORIES.map((currentItem, index) => (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TilleyDatePicker
                name="inception_date"
                label="Inception Date"
                value={state?.inception_date}
                onChange={handleChangeState}
                errorText={formErrors.inception_date}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TilleyDatePicker
                name="maturity_date"
                label="Maturity Date"
                value={state?.maturity_date}
                onChange={handleChangeState}
                errorText={formErrors.maturity_date}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Interest Rate"
                  name="interest_rate"
                  defaultValue={state?.interest_rate || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_PERCENTAGE}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="selectPaymentFrequencyLabel">Payment Frequency</InputLabel>
                <Select
                  labelId="selectPaymentFrequencyLabel"
                  value={state?.payment_frequency || ""}
                  name="payment_frequency"
                  onChange={handleChangeStateSelect}
                  label="Payment Frequency"
                >
                  {DEBT_SCHEDULE_PAYMENT_FREQUENCIES.map((currentItem, index) => (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Payment Amount"
                  name="payment_amount"
                  defaultValue={state?.payment_amount || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Original Amount"
                  name="amount"
                  defaultValue={state?.amount || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Note"
                  name="note"
                  defaultValue={state?.note || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 100 }}
                />
              </FormControl>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                During {state?.year || currentYear}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Interest Paid"
                  name="interest_paid"
                  defaultValue={state?.interest_paid || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Principal Paid"
                  name="principal_paid"
                  defaultValue={state?.principal_paid || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Year End
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Current Liability"
                  name="liability_current"
                  defaultValue={state?.liability_current || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Intermediate Liability"
                  name="liability_intermediate"
                  defaultValue={state?.liability_intermediate || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Long-Term Liability"
                  name="liability_longterm"
                  defaultValue={state?.liability_longterm || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Accrued Interest"
                  name="interest_accrued"
                  defaultValue={state?.interest_accrued || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Total Principal"
                  name="principal_total"
                  defaultValue={state?.principal_total || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Divider sx={{ width: "100%", my: 2 }} />

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Balloon Amount"
                  name="balloon_amount"
                  defaultValue={state?.balloon_amount || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Extra Principal Rate"
                  name="extra_principal_rate"
                  defaultValue={state?.extra_principal_rate || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_PERCENTAGE}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="selectDayCountConventionLabel">Day Count Convention</InputLabel>
                <Select
                  labelId="selectDayCountConventionLabel"
                  value={state?.day_count_convention || ""}
                  name="day_count_convention"
                  onChange={handleChangeStateSelect}
                  label="Day Count Convention"
                >
                  {DEBT_SCHEDULE_DAY_COUNT_CONVENTIONS.map((currentItem, index) => (
                    <MenuItem key={index} value={currentItem}>
                      {currentItem}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Interest Only Periods"
                  name="interest_only_periods"
                  defaultValue={state?.interest_only_periods || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
          <Button variant="contained" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
}
